import React, {useMemo, useState} from 'react';
import moment from 'moment';
import {graphql} from 'react-relay';
import {useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import {
	Accordion,
	Modal,
	TextField,
	FlexColumn,
	FlexRow,
	FlexItem,
	Label,
	Text,
	DropdownList,
	ButtonDropdownField,
	DateRangePicker,
	IconTooltip,
	Toggle,
	Checkbox,
	SegmentedControl,
} from '@forecast-it/design-system';
import {ForecastQueryRenderer} from '../../../../../ForecastQueryRenderer';
import {showModal, MODAL_TYPE} from '../generic_modal_conductor';
import {ClientPrimaryContactDropdown} from '../../dropdowns/client-primary-contact-dropdown/ClientPrimaryContactDropdown';
import Util from '../../../util/util';
import ValidateProjectIdMutation from '../../../../../mutations/ValidateProjectIdMutation';
import CreateProjectMutation from '../../../../../mutations/create_project_mutation';
import DuplicateProjectMutation from '../../../../../mutations/duplicate_project_mutation';
import {projectUrl} from '../../../../../directApi';
import {WinProbabilitySegmentedControl} from './WinProbabilitySegmentedControl';
import {hasModule} from '../../../util/ModuleUtil';
import CompanySetupUtil from '../../../util/CompanySetupUtil';
import {MODULE_TYPES, BUDGET_TYPE, PROJECT_STATUS, projectColors} from '../../../../../constants';

export type CreateOpportunityModalPayload = {
	clientId?: string; // reserved for Phase-2 pre-fill (FR-17)
};

export const showCreateOpportunityModal = (payload: CreateOpportunityModalPayload = {}) =>
	showModal({type: MODAL_TYPE.CREATE_OPPORTUNITY, ...payload});

// Single-item Accordion that starts open — three of these give "all open, each collapsible" (FR-12)
// without an all-open prop (which the design-system Accordion doesn't have).
const OpportunitySection = ({id, labelId, children}: {id: string; labelId: string; children: React.ReactNode}) => {
	const {formatMessage} = useIntl();
	return (
		<Accordion selectionType={Accordion.SelectionType.SINGLE} collapsible defaultValue={id} background={true} border={true}>
			<Accordion.Item value={id} label={formatMessage({id: labelId})} content="" data-cy={`section-${id}`}>
				{children}
			</Accordion.Item>
		</Accordion>
	);
};

type InnerProps = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- typed by the generated fragment once Tasks 9-11 wire it up
	viewer: any;
	closeModal: () => void;
	// Reserved for Phase-2 pre-fill (FR-17); wired up in a later task.
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	clientId?: string;
};

type ClientContact = {id: string; name: string} | null;
type PersonOption = {id: string; fullName: string};
type ClientOption = {id: string; name: string};
type RateCardOption = {id: string; name: string; currency: string};

// The design-system DateRangePicker's onChange is invoked with a single interval object
// ({startDate, endDate}, ISO date strings) — not two positional Date args. Mirrors
// new_project_modal_v2.js's onDateRangeChangeIntervalObject. Exported for direct unit testing
// since driving the real react-aria date fields through jsdom is impractical.
export const intervalToMoments = (interval: {
	startDate?: string;
	endDate?: string;
}): {startDate: moment.Moment | null; endDate: moment.Moment | null} => ({
	startDate: interval.startDate ? moment(interval.startDate) : null,
	endDate: interval.endDate ? moment(interval.endDate) : null,
});

// Loaded lazily (only when the Duplicate toggle is on) so opening the modal doesn't fetch the full
// project list. Replaces the legacy DuplicateProjectDropdown (a non-design-system dropdown with an
// uppercase label) — "Project to Duplicate" now uses the same DropdownList + ButtonDropdownField as
// the modal's other pickers.
const duplicateProjectsQuery = graphql`
	query CreateOpportunityModalDuplicateProjects_Query {
		viewer {
			actualPersonId
			component(name: "create_opportunity_duplicate_projects")
			projects(first: 10000) {
				edges {
					node {
						id
						name
						companyProjectId
						customProjectId
						fullAccessToProject
					}
				}
			}
		}
	}
`;

export const CreateOpportunityModalInner = ({viewer, closeModal}: InnerProps) => {
	const {formatMessage} = useIntl();
	const history = useHistory();

	// Overview section state (Task 8).
	const [name, setName] = useState('');
	const [customProjectId, setCustomProjectId] = useState('');
	const [customProjectIdError, setCustomProjectIdError] = useState<string | undefined>();
	const [salesperson, setSalesperson] = useState<string | null>(null);
	const [client, setClient] = useState<string | null>(null);
	const [clientContact, setClientContact] = useState<ClientContact>(null);
	const [winChance, setWinChance] = useState(0.5);

	// Details section state (Task 9).
	// FR-10: Project Dates are optional for opportunities — no default start/end date is set.
	// Task 11 (submit) reads these via moment's .date()/.month()/.year(), so they're moment
	// objects (or null), not raw Dates — matching ProjectMainInfo/new_project_modal_v2's pattern.
	const [projectStartDate, setProjectStartDate] = useState<moment.Moment | null>(null);
	const [projectEndDate, setProjectEndDate] = useState<moment.Moment | null>(null);
	// Default to a swatch colour so one appears selected on open (the previous #7A55ED wasn't in the palette).
	const [projectColor, setProjectColor] = useState(projectColors[0]);
	const [projectLabels, setProjectLabels] = useState<string[]>([]);
	const [useDuplication, setUseDuplication] = useState(false);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- typed by the generated fragment once the duplicate flow query is wired to a fragment
	const [projectToDuplicate, setProjectToDuplicate] = useState<any>(null);
	const [duplicateSettings, setDuplicateSettings] = useState(true);
	const [duplicateTasks, setDuplicateTasks] = useState(true);
	const [duplicatePhases, setDuplicatePhases] = useState(true);
	const [duplicateTeam, setDuplicateTeam] = useState(true);
	const [duplicateAllocations, setDuplicateAllocations] = useState(true);
	const [duplicateExpenses, setDuplicateExpenses] = useState(true);
	const [duplicateRetainerPeriods, setDuplicateRetainerPeriods] = useState(true);
	const [duplicatePlaceholders, setDuplicatePlaceholders] = useState(true);
	const [duplicateColor, setDuplicateColor] = useState(true);

	// Financial Setup section state (Task 10).
	// OQ-4: Time & Materials is the default budget type for a new opportunity. Retainer and
	// Non-Billable are rendered but disabled (D6) — not selectable yet for opportunities.
	const [budgetType, setBudgetType] = useState<string>(BUDGET_TYPE.TIME_AND_MATERIALS);
	// FR-9: Price only applies to Fixed Price budgets.
	const [fixedPriceBudget, setFixedPriceBudget] = useState('');
	const rateCards: RateCardOption[] = useMemo(() => viewer.company.rateCards.edges.map(edge => edge.node), [viewer]);
	const [rateCardSelected, setRateCardSelected] = useState<RateCardOption | null>(viewer.company.defaultRateCard ?? null);
	// FR-7: Use Baseline defaults ON for new opportunities.
	const [useBaseline, setUseBaseline] = useState(true);
	// Guards against double-submit (rapid double-click would otherwise fire two create mutations).
	// Mirrors new_project_modal_v2.js's `creatingProject` state.
	const [creating, setCreating] = useState(false);

	const labels = useMemo(() => viewer.company.labels.edges.map(edge => edge.node), [viewer]);

	// Currency symbol for the Price label (e.g. USD -> "$"), derived from the company currency.
	const currencySymbol = useMemo(() => {
		try {
			return (
				new Intl.NumberFormat(undefined, {style: 'currency', currency: viewer.company.currency})
					.formatToParts(0)
					.find(part => part.type === 'currency')?.value ?? viewer.company.currency
			);
		} catch {
			return viewer.company.currency;
		}
	}, [viewer]);

	const addLabel = (labelId: string) => setProjectLabels(projectLabels.concat([labelId]));
	const removeLabel = (labelIdToRemove: string) => setProjectLabels(projectLabels.filter(id => id !== labelIdToRemove));

	const onDuplicateToggle = (checked: boolean) => {
		setUseDuplication(checked);
		if (!checked) {
			setProjectToDuplicate(null);
		}
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- duplicate project node shape comes from the untyped duplicate-projects query
	const handleDuplicateProjectSelectChange = (duplicatedProjectNode: any) => setProjectToDuplicate(duplicatedProjectNode);

	// Memoized so the 10k-row map/sort doesn't re-run on every keystroke (only when `viewer` changes).
	const persons: PersonOption[] = useMemo(
		() => viewer.company.allPersons.edges.map(edge => edge.node).filter(person => person.active),
		[viewer]
	);
	const clients: ClientOption[] = useMemo(
		() => [...viewer.company.clients.edges.map(edge => edge.node)].sort((a, b) => Util.sortAlphabetically(a.name, b.name)),
		[viewer]
	);
	// Salesperson/Client are plain chevron dropdowns (DropdownList + ButtonDropdownField), not
	// ComboBox — option value is the person/client GLOBAL id so state (`salesperson`/`client`)
	// keeps feeding the submit assembly unchanged.
	const salespersonOptions = useMemo(() => persons.map(person => ({value: person.id, label: person.fullName})), [persons]);
	const clientOptions = useMemo(() => clients.map(clientNode => ({value: clientNode.id, label: clientNode.name})), [clients]);

	const onCustomProjectIdChange = (value: string) => {
		setCustomProjectId(value);
		setCustomProjectIdError(undefined);
	};

	const onCustomProjectIdBlur = () => {
		if (customProjectId === '') {
			setCustomProjectIdError(undefined);
			return;
		}
		if (!customProjectId.match(/^[a-z0-9-]+$/i)) {
			setCustomProjectIdError(formatMessage({id: 'new_project_modal.project_id_error.alphanumeric'}));
			return;
		}
		Util.CommitMutation(ValidateProjectIdMutation, {projectId: customProjectId}, result => {
			if (result.validateProjectId.valid === 'true') {
				setCustomProjectIdError(undefined);
			} else {
				setCustomProjectIdError(formatMessage({id: 'new_project_modal.project_id_error.already_in_use'}));
			}
		});
	};

	const onClientChange = (selectedClientId: string | null) => {
		// IGNITE-490 / FR-8: a contact is scoped to its client, so changing the client clears the chosen Contact.
		setClient(selectedClientId);
		setClientContact(null);
	};

	const onDateRangeChange = (interval: {startDate?: string; endDate?: string}) => {
		const {startDate, endDate} = intervalToMoments(interval);
		setProjectStartDate(startDate);
		setProjectEndDate(endDate);
	};

	// Radix single ToggleGroup deselects on re-click (emits ''); ignore empty so a value is always held
	// (mirrors WinProbabilitySegmentedControl).
	const onBudgetTypeChange = (value: string) => {
		if (value) {
			setBudgetType(value);
		}
	};

	const onRateCardChange = (rateCardId: string | null) =>
		setRateCardSelected(rateCards.find(rateCard => rateCard.id === rateCardId) ?? null);

	// FR-14: on success, close the modal and navigate straight to the new project page. Unlike
	// new_project_modal_v2's navigateToNewProject, this is the simple path — no team-assignment
	// step and no budgetType/useBaseline-dependent suffix (/retainer-tracking, /baseline, /scoping).
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- result shape comes from the untyped CreateProjectMutation
	const onSuccess = (result: any) => {
		// Payload-level errors (e.g. Sage Intacct) arrive here, not onError — and on that path
		// `project` is null, so guard before reading it (mirrors new_project_modal_v2.js).
		if (Util.checkForSageErrorAndShowModal(result.createProject.errors, false)) {
			setCreating(false);
			return;
		}
		const node = result.createProject.project.node;
		closeModal();
		history.push(projectUrl(node.companyProjectId, node.customProjectId));
	};

	// Duplicate flow returns the new project under `duplicateProject` (not `createProject`); navigate
	// to it exactly like the create path. Mirrors new_project_modal_v2.js's duplicate branch.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- result shape comes from the untyped DuplicateProjectMutation
	const onSuccessDuplicate = (result: any) => {
		if (Util.checkForSageErrorAndShowModal(result.duplicateProject.errors, false)) {
			setCreating(false);
			return;
		}
		const node = result.duplicateProject.project.node;
		closeModal();
		history.push(projectUrl(node.companyProjectId, node.customProjectId));
	};

	const handleCreate = () => {
		if (creating) return; // double-submit guard
		// Fields common to both paths. `status` is ALWAYS OPPORTUNITY so a duplicated project lands
		// as an opportunity regardless of the source project's stage (DuplicateProjectInput gained
		// salespersonId/baselineWinChance/status for IGNITE-572).
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Create/DuplicateProjectInput aren't typed on the JS mutation modules
		const input: any = {
			name,
			customProjectId: customProjectId || undefined,
			salespersonId: salesperson || undefined,
			baselineWinChance: winChance,
			status: PROJECT_STATUS.OPPORTUNITY,
			clientId: client || undefined,
			clientContactId: clientContact?.id,
		};
		// FR-10: dates are optional — only send them when set (mirrors new_project_modal_v2.js).
		if (projectStartDate) {
			input.projectStartDay = projectStartDate.date();
			input.projectStartMonth = projectStartDate.month() + 1;
			input.projectStartYear = projectStartDate.year();
		}
		if (projectEndDate) {
			input.projectEndDay = projectEndDate.date();
			input.projectEndMonth = projectEndDate.month() + 1;
			input.projectEndYear = projectEndDate.year();
		}
		setCreating(true);
		if (useDuplication && projectToDuplicate) {
			// Issue 1 fix: duplication MUST go through DuplicateProjectMutation — createProject ignores
			// projectId/duplicate* fields. Financial (budget type/price/rate card/baseline), colour and
			// labels are hidden in duplicate mode (PM, IGNITE-572) and inherited from the source project
			// — none of their form data is sent (the mutation also omits labels/rateCardId).
			input.projectId = projectToDuplicate.id;
			input.duplicateSettings = duplicateSettings;
			input.duplicatePhases = duplicatePhases;
			input.duplicateTasks = duplicateTasks;
			input.duplicateProjectPersons = duplicateTeam;
			input.duplicateAllocations = duplicateAllocations;
			input.duplicateExpenses = duplicateExpenses;
			input.duplicateRetainerPeriods = duplicateRetainerPeriods;
			input.duplicatePlaceholders = duplicatePlaceholders;
			input.duplicateColor = duplicateColor;
			Util.CommitMutation(DuplicateProjectMutation, input, onSuccessDuplicate);
			return;
		}
		// Create-from-scratch path: financials (budget type/price), colour and labels are create-only.
		// Per PM (IGNITE-572) the Financial and Labels sections are hidden while duplicating and their
		// form data is not sent — a duplicate inherits these from the source project.
		input.budgetType = budgetType;
		// Only send budget when a Fixed Price value parses to a real number — parseFloat('') and
		// parseFloat('.') are NaN, which must not reach the GraphQLFloat field (mirrors
		// new_project_modal_v2.js). The `!isNaN` guard covers a lone "." that the input regex allows.
		if (budgetType === BUDGET_TYPE.FIXED_PRICE && fixedPriceBudget) {
			const parsedBudget = parseFloat(fixedPriceBudget);
			if (!isNaN(parsedBudget)) {
				input.budget = parsedBudget;
			}
		}
		input.useBaseline = useBaseline;
		input.rateCardId = rateCardSelected?.id;
		input.projectColor = projectColor;
		input.labels = projectLabels;
		Util.CommitMutation(CreateProjectMutation, input, onSuccess);
	};

	// A partial hex from the manual input (e.g. "#AB") passes the as-you-type regex but the backend
	// rejects it — block submit until it's a full #RRGGBB. Only relevant in create mode (colour is
	// hidden and not sent in duplicate mode).
	const colorInvalid = !useDuplication && !/^#[0-9A-Fa-f]{6}$/.test(projectColor);

	return (
		<Modal onCloseModal={closeModal} size={Modal.SIZE.EXTRA_LARGE}>
			{/* Modal.Title (Radix Dialog.Title) is itself the <h2> and already applies heading-3 font —
				don't nest a <Heading> (h3) inside it. */}
			<Modal.Title>{formatMessage({id: 'opportunity.create_title'})}</Modal.Title>
			<Modal.Content data-cy="create-opportunity-modal">
				{/* FR-12: three sections, all expanded on mount, each individually collapsible.
					The design-system Accordion's defaultValue opens only ONE item, so use three
					independent single-item Accordions (each defaultValue-open) rather than one. */}
				<OpportunitySection id="overview" labelId="opportunity.section.overview">
					<FlexColumn gap="l">
						<FlexRow>
							<TextField
								data-cy="opportunity-name"
								label={formatMessage({id: 'opportunity.field.name'})}
								value={name}
								onValueChange={setName}
								maxLength={191}
							/>
						</FlexRow>
						<FlexRow gap="l">
							<FlexItem flex="1 1 0">
								<TextField
									data-cy="opportunity-id"
									label={formatMessage({id: 'opportunity.field.id'})}
									value={customProjectId}
									onValueChange={onCustomProjectIdChange}
									onBlur={onCustomProjectIdBlur}
									hasError={!!customProjectIdError}
									errorText={customProjectIdError}
									maxLength={20}
									placeholder={`P${viewer.company.projectLastSequence + 1}`}
								/>
							</FlexItem>
							<FlexItem flex="1 1 0">
								<FlexColumn gap="xs">
									<Label>{formatMessage({id: 'opportunity.field.salesperson'})}</Label>
									<DropdownList>
										<DropdownList.Trigger
											data-cy="opportunity-salesperson"
											aria-label={formatMessage({id: 'opportunity.field.salesperson'})}
										>
											<ButtonDropdownField>
												<Text>
													{salespersonOptions.find(o => o.value === salesperson)?.label ??
														formatMessage({id: 'common.none'})}
												</Text>
											</ButtonDropdownField>
										</DropdownList.Trigger>
										<DropdownList.Content align="start" useTriggerWidth>
											<DropdownList.Item
												data-cy="opportunity-salesperson-none"
												onClick={() => setSalesperson(null)}
											>
												<Text>{formatMessage({id: 'common.none'})}</Text>
											</DropdownList.Item>
											{salespersonOptions.map(option => (
												<DropdownList.Item
													key={option.value}
													data-cy={`opportunity-salesperson-${option.value}`}
													onClick={() => setSalesperson(option.value)}
												>
													<Text>{option.label}</Text>
												</DropdownList.Item>
											))}
										</DropdownList.Content>
									</DropdownList>
								</FlexColumn>
							</FlexItem>
						</FlexRow>
						<FlexRow gap="l">
							<FlexItem flex="1 1 0">
								<FlexColumn gap="xs">
									<Label>{formatMessage({id: 'common.client'})}</Label>
									<DropdownList>
										<DropdownList.Trigger
											data-cy="opportunity-client"
											aria-label={formatMessage({id: 'common.client'})}
										>
											<ButtonDropdownField>
												<Text>
													{clientOptions.find(o => o.value === client)?.label ??
														formatMessage({id: 'common.none'})}
												</Text>
											</ButtonDropdownField>
										</DropdownList.Trigger>
										<DropdownList.Content align="start" useTriggerWidth>
											<DropdownList.Item
												data-cy="opportunity-client-none"
												onClick={() => onClientChange(null)}
											>
												<Text>{formatMessage({id: 'common.none'})}</Text>
											</DropdownList.Item>
											{clientOptions.map(option => (
												<DropdownList.Item
													key={option.value}
													data-cy={`opportunity-client-${option.value}`}
													onClick={() => onClientChange(option.value)}
												>
													<Text>{option.label}</Text>
												</DropdownList.Item>
											))}
										</DropdownList.Content>
									</DropdownList>
								</FlexColumn>
							</FlexItem>
							<FlexItem flex="1 1 0">
								<FlexColumn gap="xs">
									<Label>{formatMessage({id: 'common.contact'})}</Label>
									{client ? (
										<ClientPrimaryContactDropdown
											key={client}
											clientId={client}
											value={clientContact}
											onChange={setClientContact}
											hideLabel
											dataCy="opportunity-contact"
										/>
									) : (
										<div data-cy="opportunity-contact">
											<ButtonDropdownField disabled>
												<Text>{formatMessage({id: 'project_settings.select_contact'})}</Text>
											</ButtonDropdownField>
										</div>
									)}
								</FlexColumn>
							</FlexItem>
						</FlexRow>
						<FlexRow>
							<FlexColumn gap="xs">
								<Label>{formatMessage({id: 'opportunity.field.win_probability'})}</Label>
								<WinProbabilitySegmentedControl value={winChance} onChange={setWinChance} />
							</FlexColumn>
						</FlexRow>
					</FlexColumn>
				</OpportunitySection>
				<OpportunitySection id="details" labelId="opportunity.section.details">
					<FlexColumn gap="l">
						<FlexRow gap="l">
							<FlexItem flex="1 1 0">
								<FlexColumn gap="xs">
									<FlexRow gap="xs" alignItems="center">
										<Label>{formatMessage({id: 'common.dates'})}</Label>
										<IconTooltip>
											<IconTooltip.Item title={formatMessage({id: 'opportunity.dates_tooltip'})} />
										</IconTooltip>
									</FlexRow>
									<DateRangePicker
										data-cy="opportunity-dates"
										aria-label={formatMessage({id: 'common.dates'})}
										startDate={projectStartDate?.format('YYYY-MM-DD')}
										endDate={projectEndDate?.format('YYYY-MM-DD')}
										onChange={onDateRangeChange}
									/>
								</FlexColumn>
							</FlexItem>
							{useDuplication ? null : (
								<FlexItem flex="1 1 0">
									<FlexColumn gap="xs">
										<Label>{formatMessage({id: 'common.color'})}</Label>
										<FlexRow gap="s" alignItems="center">
											{projectColors.slice(0, 6).map(colorOption => (
												<button
													key={colorOption}
													type="button"
													data-cy={`opportunity-color-${colorOption}`}
													aria-label={colorOption}
													aria-pressed={projectColor === colorOption}
													onClick={() => setProjectColor(colorOption)}
													style={{
														width: 32,
														height: 32,
														flex: '0 0 auto',
														borderRadius: 4,
														cursor: 'pointer',
														backgroundColor: colorOption,
														border:
															projectColor === colorOption
																? '2px solid #6C5CE7'
																: '1px solid #E6E7EB',
													}}
												/>
											))}
											<FlexItem flex="1 1 0">
												<TextField
													data-cy="opportunity-color-hex"
													aria-label={formatMessage({id: 'common.color'})}
													value={projectColor}
													placeholder="#000000"
													onValueChange={(value: string) => {
														if (/^#([0-9A-Fa-f]{0,6})$/.test(value)) {
															setProjectColor(value);
														}
													}}
												/>
											</FlexItem>
										</FlexRow>
									</FlexColumn>
								</FlexItem>
							)}
						</FlexRow>
						{/* Labels — full width, create mode only. Per PM (IGNITE-572): hidden while duplicating —
							the copy inherits the source project's labels, so a value picked here is not applied. */}
						{useDuplication ? null : (
							<FlexRow>
								<FlexItem flex="1 1 100%">
									<FlexColumn gap="xs">
										<Label>{formatMessage({id: 'project_settings.project-labels'})}</Label>
										<DropdownList>
											<DropdownList.Trigger
												data-cy="opportunity-labels"
												aria-label={formatMessage({id: 'project_settings.project-labels'})}
											>
												<ButtonDropdownField>
													<Text>
														{projectLabels.length
															? `${formatMessage({id: 'common.added'})} (${projectLabels.length})`
															: formatMessage({id: 'settings_labels.add-label'})}
													</Text>
												</ButtonDropdownField>
											</DropdownList.Trigger>
											<DropdownList.Content align="start" useTriggerWidth>
												{labels.map(labelOption => (
													<DropdownList.CheckboxItem
														key={labelOption.id}
														data-cy={`opportunity-label-${labelOption.id}`}
														checked={projectLabels.includes(labelOption.id)}
														onChange={() =>
															projectLabels.includes(labelOption.id)
																? removeLabel(labelOption.id)
																: addLabel(labelOption.id)
														}
													>
														<FlexRow gap="s" alignItems="center">
															<div
																style={{
																	width: 12,
																	height: 12,
																	borderRadius: 2,
																	backgroundColor: labelOption.color,
																}}
															/>
															<Text>{labelOption.name}</Text>
														</FlexRow>
													</DropdownList.CheckboxItem>
												))}
											</DropdownList.Content>
										</DropdownList>
									</FlexColumn>
								</FlexItem>
							</FlexRow>
						)}
						<FlexRow gap="s" alignItems="center">
							{/* Figma: switch on the left, label on the right. */}
							<Toggle checked={useDuplication} onChange={onDuplicateToggle} cy="opportunity-duplicate-toggle" />
							<Text>{formatMessage({id: 'opportunity.duplicate_existing_project'})}</Text>
						</FlexRow>
						{useDuplication ? (
							<FlexColumn gap="l">
								<FlexRow>
									<FlexItem flex="1 1 100%">
										<div data-testid="duplicate-project-dropdown">
											<FlexColumn gap="xs">
												<Label>{formatMessage({id: 'opportunity.project_to_duplicate'})}</Label>
												<ForecastQueryRenderer
													key="query-render-duplicate-projects"
													query={duplicateProjectsQuery}
													customLoader={() => (
														<ButtonDropdownField disabled>
															<Text>{formatMessage({id: 'common.loading'})}</Text>
														</ButtonDropdownField>
													)}
													// eslint-disable-next-line @typescript-eslint/no-explicit-any -- typed by the generated query
													render={(relayProps: any) => {
														const projectOptions = relayProps.viewer.projects.edges
															.map((edge: {node: any}) => edge.node) // eslint-disable-line @typescript-eslint/no-explicit-any
															.filter(
																(node: {fullAccessToProject: boolean}) =>
																	node.fullAccessToProject
															);
														return (
															<DropdownList>
																<DropdownList.Trigger
																	data-cy="opportunity-duplicate-project"
																	aria-label={formatMessage({
																		id: 'opportunity.project_to_duplicate',
																	})}
																>
																	<ButtonDropdownField>
																		<Text>
																			{projectToDuplicate
																				? `${projectToDuplicate.customProjectId} ${projectToDuplicate.name}`
																				: formatMessage({id: 'common.none'})}
																		</Text>
																	</ButtonDropdownField>
																</DropdownList.Trigger>
																<DropdownList.Content align="start" useTriggerWidth>
																	<DropdownList.Item
																		data-cy="opportunity-duplicate-project-none"
																		onClick={() => handleDuplicateProjectSelectChange(null)}
																	>
																		<Text>{formatMessage({id: 'common.none'})}</Text>
																	</DropdownList.Item>
																	{/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
																	{projectOptions.map((node: any) => (
																		<DropdownList.Item
																			key={node.id}
																			data-cy={`opportunity-duplicate-project-${node.id}`}
																			onClick={() =>
																				handleDuplicateProjectSelectChange(node)
																			}
																		>
																			<Text>{`${node.customProjectId} ${node.name}`}</Text>
																		</DropdownList.Item>
																	))}
																</DropdownList.Content>
															</DropdownList>
														);
													}}
												/>
											</FlexColumn>
										</div>
									</FlexItem>
								</FlexRow>
								{projectToDuplicate ? (
									<FlexColumn gap="s">
										<Label>{formatMessage({id: 'common.duplicate'})}:</Label>
										{/* Figma 82-7220: options grouped into columns. "Deliverables" is omitted — DuplicateProjectInput
has no duplicateDeliverables field (deliverables copy with Phases/baseline). */}
										<FlexRow gap="l" alignItems="flex-start">
											<FlexItem flex="1 1 0">
												<FlexColumn gap="xs">
													<Label>
														{formatMessage({id: 'opportunity.duplicate_group.structure'})}
													</Label>
													<Checkbox
														checked={duplicateTasks}
														onChange={checked => setDuplicateTasks(!!checked)}
														labelText={formatMessage({id: 'opportunity.duplicate_item.tasks'})}
													/>
													<Checkbox
														checked={duplicatePhases}
														onChange={checked => setDuplicatePhases(!!checked)}
														labelText={formatMessage({id: 'opportunity.duplicate_item.phases'})}
													/>
												</FlexColumn>
											</FlexItem>
											<FlexItem flex="1 1 0">
												<FlexColumn gap="xs">
													<Label>
														{formatMessage({id: 'opportunity.duplicate_group.resourcing'})}
													</Label>
													<Checkbox
														checked={duplicateTeam}
														onChange={checked => setDuplicateTeam(!!checked)}
														labelText={formatMessage({
															id: 'opportunity.duplicate_item.team_members',
														})}
													/>
													{duplicateTeam ? (
														<Checkbox
															checked={duplicateAllocations}
															onChange={checked => setDuplicateAllocations(!!checked)}
															labelText={formatMessage({
																id: 'opportunity.duplicate_item.allocations',
															})}
														/>
													) : null}
													{hasModule(MODULE_TYPES.PLACEHOLDERS) ? (
														<Checkbox
															checked={duplicatePlaceholders}
															onChange={checked => setDuplicatePlaceholders(!!checked)}
															labelText={formatMessage({
																id: 'opportunity.duplicate_item.placeholders',
															})}
														/>
													) : null}
												</FlexColumn>
											</FlexItem>
											{CompanySetupUtil.hasFinance() ? (
												<FlexItem flex="1 1 0">
													<FlexColumn gap="xs">
														<Label>
															{formatMessage({id: 'opportunity.duplicate_group.financials'})}
														</Label>
														<Checkbox
															checked={duplicateExpenses}
															onChange={checked => setDuplicateExpenses(!!checked)}
															labelText={formatMessage({id: 'project_budget.expenses'})}
														/>
														<Checkbox
															checked={duplicateRetainerPeriods}
															onChange={checked => setDuplicateRetainerPeriods(!!checked)}
															labelText={formatMessage({
																id: 'opportunity.duplicate_item.retainer_periods',
															})}
														/>
													</FlexColumn>
												</FlexItem>
											) : null}
											<FlexItem flex="1 1 0">
												<FlexColumn gap="xs">
													<Label>{formatMessage({id: 'opportunity.duplicate_group.setup'})}</Label>
													<Checkbox
														checked={duplicateSettings}
														onChange={checked => setDuplicateSettings(!!checked)}
														labelText={formatMessage({id: 'duplicate-modal.settings-v2'})}
													/>
													<Checkbox
														checked={duplicateColor}
														onChange={checked => setDuplicateColor(!!checked)}
														labelText={formatMessage({
															id: 'integration.project_templates.project_color',
														})}
													/>
												</FlexColumn>
											</FlexItem>
										</FlexRow>
									</FlexColumn>
								) : null}
							</FlexColumn>
						) : null}
					</FlexColumn>
				</OpportunitySection>
				{/* Financial — create mode only. Per PM (IGNITE-572): hidden while duplicating; budget type,
					price, rate card and baseline all come from the source project. */}
				{useDuplication ? null : (
					<OpportunitySection id="financial" labelId="opportunity.section.financial">
						<FlexColumn gap="l">
							<FlexRow>
								<FlexColumn gap="xs">
									<Label>{formatMessage({id: 'opportunity.field.budget_type'})}</Label>
									{/* D6: only Fixed Price and Time & Materials are selectable for opportunities today;
									Retainer and Non-Billable are shown disabled. */}
									<SegmentedControl
										value={budgetType}
										onValueChange={onBudgetTypeChange}
										data-cy="opportunity-budget-type"
									>
										<SegmentedControl.Item
											value={BUDGET_TYPE.FIXED_PRICE}
											label={formatMessage({id: 'common.fixed_price'})}
											data-cy="budget-FIXED_PRICE"
										>
											{formatMessage({id: 'common.fixed_price'})}
										</SegmentedControl.Item>
										<SegmentedControl.Item
											value={BUDGET_TYPE.TIME_AND_MATERIALS}
											label={formatMessage({id: 'common.time_material'})}
											data-cy="budget-TIME_AND_MATERIALS"
										>
											{formatMessage({id: 'common.time_material'})}
										</SegmentedControl.Item>
										<SegmentedControl.Item
											value={BUDGET_TYPE.RETAINER}
											label={formatMessage({id: 'common.retainer'})}
											disabled
											data-cy="budget-RETAINER"
										>
											{formatMessage({id: 'common.retainer'})}
										</SegmentedControl.Item>
										<SegmentedControl.Item
											value={BUDGET_TYPE.NON_BILLABLE}
											label={formatMessage({id: 'common.non-billable'})}
											disabled
											data-cy="budget-NON_BILLABLE"
										>
											{formatMessage({id: 'common.non-billable'})}
										</SegmentedControl.Item>
									</SegmentedControl>
								</FlexColumn>
							</FlexRow>
							<FlexRow gap="l">
								{budgetType === BUDGET_TYPE.FIXED_PRICE ? (
									<FlexItem flex="1 1 0">
										{/* Design-system TextField (matches the dropdown height + the ID field) — its label
										associates with the input, and it lines up with Rate Card the way ID lines up with
										Salesperson. allowedCharactersRegex allows digits and a single decimal point
										(so "1.2.3" can't be typed); the submit handler still guards a lone ".". */}
										<TextField
											data-cy="opportunity-fixed-price-budget"
											label={formatMessage({id: 'opportunity.field.price'}, {currency: currencySymbol})}
											value={fixedPriceBudget}
											onValueChange={setFixedPriceBudget}
											placeholder="0"
											allowedCharactersRegex="^\d*\.?\d*$"
										/>
									</FlexItem>
								) : null}
								<FlexItem flex="1 1 0">
									<FlexColumn gap="xs">
										<Label>{formatMessage({id: 'common.rate_card'})}</Label>
										<DropdownList>
											<DropdownList.Trigger
												data-cy="opportunity-rate-card"
												aria-label={formatMessage({id: 'common.rate_card'})}
											>
												<ButtonDropdownField>
													<Text>{rateCardSelected?.name ?? formatMessage({id: 'common.none'})}</Text>
												</ButtonDropdownField>
											</DropdownList.Trigger>
											<DropdownList.Content align="start" useTriggerWidth>
												{rateCards.map(rateCard => (
													<DropdownList.Item
														key={rateCard.id}
														data-cy={`opportunity-rate-card-${rateCard.id}`}
														onClick={() => onRateCardChange(rateCard.id)}
													>
														<Text>{rateCard.name}</Text>
													</DropdownList.Item>
												))}
											</DropdownList.Content>
										</DropdownList>
									</FlexColumn>
								</FlexItem>
							</FlexRow>
							{/* Use Baseline: switch + sibling label in a centred FlexRow so the click target is the toggle,
								not the full-width row, and the switch lines up with the label (mirrors the duplicate toggle). */}
							<FlexRow gap="s" alignItems="center">
								<Text>{formatMessage({id: 'opportunity.field.use_baseline'})}</Text>
								<Toggle checked={useBaseline} onChange={setUseBaseline} cy="opportunity-use-baseline" />
							</FlexRow>
						</FlexColumn>
					</OpportunitySection>
				)}
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton
					onPress={handleCreate}
					dataCy="create-opportunity-submit"
					disabled={!name.trim() || creating || !!customProjectIdError || colorInvalid}
				>
					{formatMessage({id: 'opportunity.create_button'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal} dataCy="modal-cancel">
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

const createOpportunityModalQuery = graphql`
	query CreateOpportunityModal_Query {
		viewer {
			actualPersonId
			component(name: "create_opportunity_modal")
			company {
				id
				projectLastSequence
				currency
				defaultRateCard {
					id
					name
					currency
				}
				rateCards(first: 10000) {
					edges {
						node {
							id
							name
							currency
						}
					}
				}
				clients(first: 10000) {
					edges {
						node {
							id
							name
						}
					}
				}
				allPersons(first: 10000, onlyActive: true, excludeClientUsers: true) {
					edges {
						node {
							id
							fullName
							active
						}
					}
				}
				labels(first: 10000, labelType: PROJECT) {
					edges {
						node {
							id
							name
							color
						}
					}
				}
				modules {
					moduleType
				}
			}
		}
	}
`;

const CreateOpportunityModal = (props: {closeModal: () => void; clientId?: string}) => (
	<ForecastQueryRenderer
		key="query-render-createOpportunityModal"
		query={createOpportunityModalQuery}
		modal={true}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- typed by the generated query once Tasks 8-11 wire it up
		render={(relayProps: any) => (
			<CreateOpportunityModalInner viewer={relayProps.viewer} closeModal={props.closeModal} clientId={props.clientId} />
		)}
	/>
);

export default CreateOpportunityModal;
